// AmsReports.tsx — live reporting, and an honest catalog of what is not built.
//
// THE TRANSITION MATRIX is the reason `arrears_stage_snapshots` was written from the first
// migration. It shows how many accounts moved between ageing buckets between two dates, and it
// CANNOT be reconstructed from current balances — every day the snapshot is not recorded is a
// month of movement analysis that can never be produced.
//
// The catalog lists all fourteen views from the plan with what each still needs. A tile that opens
// onto nothing is worse than one that says plainly it is not built.
import { useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { api, ApiError } from '../../lib/api'
import { BUCKETS, dateOf, money } from '../../lib/format'
import DataTable from '../DataTable'

type Data = Awaited<ReturnType<typeof api.ams.reports>>
const ORDER = ['current', 'd30', 'd60', 'd90', 'cleared']

export default function AmsReports() {
  const { me } = useAuth()
  const org = me?.org?.id
  const [d, setD] = useState<Data | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!org) return
    api.ams.reports(org).then(setD)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)))
  }, [org])

  if (error) return <div className="page-state page-state-error">Could not load: {error}</div>
  if (!d) return <div className="page-state">Loading…</div>

  const cell = (f: string, t: string) =>
    d.transition.cells.find(c => c.from_bucket === f && c.to_bucket === t)?.n ?? 0
  const froms = ORDER.filter(f => d.transition.cells.some(c => c.from_bucket === f))
  const tos = ORDER.filter(t => d.transition.cells.some(c => c.to_bucket === t))

  const divisions = [...new Set(d.ageing.map(a => a.division))].sort()
  // Lifted out of the row so the same lookups serve the table and the phone cards from one
  // definition rather than being recomputed inside a map that now renders twice.
  const nOf = (dv: string, b: string) =>
    d.ageing.find(a => a.division === dv && a.bucket === b)?.count ?? 0
  const centsOf = (dv: string) =>
    d.ageing.filter(a => a.division === dv).reduce((sum, a) => sum + a.cents, 0)

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Reports</h2>
          <p className="muted">
            Generated from live case data. The same figures serve internal review and the MND
            submission, so there is one set of numbers rather than two.
          </p>
        </div>
      </header>

      <section className="card">
        <h3>Delinquency movement</h3>
        <p className="muted sm">
          {d.transition.fromDate
            ? `How accounts moved between ${dateOf(d.transition.fromDate)} and ${dateOf(d.transition.toDate)}.`
            : 'Needs at least two daily snapshots.'}{' '}
          Built from a daily record of each account&apos;s bucket — it cannot be reconstructed from
          current balances, which is why the snapshot is written from day one.
        </p>
        {/* Stays a table at every width. This is a cross-tabulation — a row means
            nothing without its column headings — so stacking it would destroy the information.
            The "from" column is pinned instead, so the labels survive a sideways scroll. */}
        {froms.length > 0 && (
          <div className="matrix-scroll">
          <table className="tbl matrix">
            <thead>
              <tr>
                <th>From ↓ / To →</th>
                {tos.map(t => <th key={t}>{t === 'cleared' ? 'Cleared' : BUCKETS[t] ?? t}</th>)}
              </tr>
            </thead>
            <tbody>
              {froms.map(f => (
                <tr key={f}>
                  <td><b>{BUCKETS[f] ?? f}</b></td>
                  {tos.map(t => {
                    const n = cell(f, t)
                    // Worse is worse: a move to a later bucket is the number to notice.
                    const worse = ORDER.indexOf(t) > ORDER.indexOf(f) && t !== 'cleared'
                    return (
                      <td key={t} className={n === 0 ? 'muted' : worse ? 'worse' : ''}>
                        {n ? n.toLocaleString() : '—'}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      <div className="grid-2">
        <section className="card">
          <h3>Ageing by division</h3>
          <DataTable
            rows={divisions}
            rowKey={dv => dv}
            empty="No ageing data."
            columns={[
              { key: 'div', header: 'Division', role: 'primary', cell: dv => dv },
              { key: 'd30', header: '30–59', num: true, cell: dv => nOf(dv, 'd30').toLocaleString() },
              { key: 'd60', header: '60–89', num: true, cell: dv => nOf(dv, 'd60').toLocaleString() },
              { key: 'd90', header: '90+', num: true,
                cell: dv => <span className={nOf(dv, 'd90') ? 'worse' : ''}>{nOf(dv, 'd90').toLocaleString()}</span> },
              { key: 'owed', header: 'Owed', num: true, cell: dv => money(centsOf(dv)) },
            ]}
          />
        </section>

        <section className="card">
          <h3>Case workflow</h3>
          <dl className="kv">
            <dt>Visits logged</dt><dd>{d.workflow.visits_logged?.toLocaleString()}</dd>
            <dt>Visits due</dt><dd>{d.workflow.visits_due?.toLocaleString()}</dd>
            <dt>Approvals pending</dt><dd>{d.workflow.approvals_pending}</dd>
            <dt>Approvals blocked by rule</dt><dd>{d.workflow.approvals_blocked}</dd>
            <dt>Cases suppressed</dt><dd>{d.workflow.suppressed?.toLocaleString()}</dd>
            <dt>Case events recorded</dt><dd>{d.workflow.events?.toLocaleString()}</dd>
          </dl>
        </section>
      </div>

      <section className="card">
        <h3>The fourteen analytical views</h3>
        <p className="muted sm">
          What is built, and what each of the rest still needs. Stated rather than implied.
        </p>
        <table className="tbl">
          <thead><tr><th>#</th><th>View</th><th>Status</th></tr></thead>
          <tbody>
            {d.catalog.map(r => (
              <tr key={r.n}>
                <td className="muted">{r.n}</td>
                <td>{r.title}</td>
                <td>
                  {r.status === 'built'
                    ? <span className="tag c2">built</span>
                    : <span className="muted sm">{r.status}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}
